import {
	ArrowDownIcon,
	ArrowLeftIcon,
	ArrowRightIcon,
	ArrowUpIcon,
	ArrowsPointingOutIcon,
	ExclamationCircleIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
	GQLProductFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSidePrintReferencePositionType,
	GQLProductSideSideType,
} from '@theprintbar/graphql';
import { DecorationMeta, Merch } from '@theprintbar/shared';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import type { CreativeEditorInstance } from '../../CreativeEditor';

interface ArtworkInfoPanelProps {
	merch?: Merch;
	product?: GQLProductFragment;
	activeSide?: GQLProductSideSideType;
	mobileView?: boolean;
	editor?: CreativeEditorInstance;
}

enum ArtworkStatus {
	Empty,
	LowRes,
	Oversized,
	Good,
}

// CE.SDK (>=1.64) applies `isolation: isolate` to its editor container, so DOM outside the shadow
// root cannot stack under its panels. IMG.LY's fix is to mount overlay UI inside the canvas
// container's overlay layer (the child with its own `isolation: isolate`) so it paints above the
// canvas and below the panels/layout.
const STYLE_ID = 'tpb-artwork-info-styles';
const OVERLAY_STYLES = `
	.tpb-pills {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 1rem;
		display: flex;
		gap: 0.5rem;
		align-items: center;
		justify-content: center;
		font-size: 0.75rem;
		font-family: 'Diatype', ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
		color: #011CE7;
		pointer-events: none;
	}
	@media (min-width: 1024px) {
		.tpb-pills { left: 4rem; }
		.tpb-pills--mobile { left: 0; }
	}
	.tpb-pill {
		display: flex;
		align-items: center;
		padding: 0.25rem 1rem;
		border: 1px solid #011CE7;
		border-radius: 9999px;
		background: #ECFBEC;
		color: #011CE7;
		pointer-events: auto;
	}
	.tpb-pill--danger { background: #FF5A5A; color: #fff; }
	.tpb-pill--warning { background: #FFA159; color: #fff; }
	.tpb-pill__icon { width: 1.5rem; height: 1.5rem; margin-right: 0.5rem; flex-shrink: 0; color: inherit; }
	.tpb-pill__icon--lg { width: 1.75rem; height: 1.75rem; }
	.tpb-pill__label {
		margin: 0;
		padding: 0;
		font-size: inherit;
		font-weight: 600;
		line-height: 1;
		text-transform: uppercase;
		color: inherit;
	}
	.tpb-pill__value {
		margin: 0;
		padding: 0;
		font-size: inherit;
		font-weight: 400;
		line-height: 1.25;
		color: inherit;
	}
`;

function findCanvasOverlay(): { shadow: ShadowRoot; layer: HTMLElement } | null {
	const shadow = document.querySelector<HTMLElement>('#root-shadow')?.shadowRoot;
	const canvasContainer = shadow?.querySelector('[data-ubq-canvas="ubq-canvas-container"]');
	if (!shadow || !canvasContainer) return null;
	const layer = (Array.from(canvasContainer.children) as HTMLElement[]).find(
		c => getComputedStyle(c).isolation === 'isolate',
	);
	return layer ? { shadow, layer } : null;
}

function injectOverlayStylesOnce(shadow: ShadowRoot): void {
	if (shadow.querySelector(`#${STYLE_ID}`)) return;
	const style = document.createElement('style');
	style.id = STYLE_ID;
	style.textContent = OVERLAY_STYLES;
	shadow.appendChild(style);
}

export function ArtworkInfoPanel({ merch, product, activeSide, mobileView, editor }: ArtworkInfoPanelProps) {
	const [status, setStatus] = useState(ArtworkStatus.Empty);
	const [meta, setMeta] = useState<DecorationMeta | undefined>();
	const [overlayLayer, setOverlayLayer] = useState<HTMLElement | null>(null);

	const productSide = product?.sides?.find(s => s.side === activeSide);

	useEffect(() => {
		if (!editor) {
			setOverlayLayer(null);
			return;
		}
		const tryFind = () => {
			const result = findCanvasOverlay();
			if (!result) return false;
			injectOverlayStylesOnce(result.shadow);
			setOverlayLayer(result.layer);
			return true;
		};
		if (tryFind()) return;
		const id = window.setInterval(() => {
			if (tryFind()) window.clearInterval(id);
		}, 100);
		return () => window.clearInterval(id);
	}, [editor]);

	useEffect(() => {
		const decoration = merch?.decorations?.find(d => d.side === activeSide);
		if (!decoration?.artwork?.size) {
			setStatus(ArtworkStatus.Empty);
			return;
		}
		setMeta(decoration.meta);

		if (
			decoration.type === GQLProductSideAvailableDecorationType.Emb &&
			Math.max(decoration.meta.width, decoration.meta.height) > 150
		) {
			setStatus(ArtworkStatus.Oversized);
			return;
		}

		if (decoration.meta.graphicData?.find(g => !!g.dpi && g.dpi < 72)) {
			setStatus(ArtworkStatus.LowRes);
			return;
		}

		setStatus(ArtworkStatus.Good);
	}, [merch, activeSide]);

	if (!overlayLayer || status === ArtworkStatus.Empty) return null;

	const pills = (
		<div className={`tpb-pills${mobileView ? ' tpb-pills--mobile' : ''}`}>
			{status === ArtworkStatus.Good && (
				<>
					<div className="tpb-pill">
						<ArrowsPointingOutIcon className="tpb-pill__icon" />
						<div>
							<h5 className="tpb-pill__label">Print Size</h5>
							<p className="tpb-pill__value">
								{meta?.width}mm x {meta?.height}mm
							</p>
						</div>
					</div>
					{productSide && meta && (
						<div className="tpb-pill">
							{productSide.printReferencePosition === GQLProductSidePrintReferencePositionType.Bottom ? (
								<ArrowUpIcon className="tpb-pill__icon" />
							) : productSide.printReferencePosition === GQLProductSidePrintReferencePositionType.Left ? (
								<ArrowRightIcon className="tpb-pill__icon" />
							) : productSide.printReferencePosition === GQLProductSidePrintReferencePositionType.Right ? (
								<ArrowLeftIcon className="tpb-pill__icon" />
							) : (
								<ArrowDownIcon className="tpb-pill__icon" />
							)}
							<div>
								<h5 className="tpb-pill__label">Distance from {productSide.printReferenceText.toLowerCase()}</h5>
								<p className="tpb-pill__value">
									{((productSide.printReferencePosition === GQLProductSidePrintReferencePositionType.Bottom
										? productSide.printHeight - (meta.bottom || 0)
										: productSide.printReferencePosition === GQLProductSidePrintReferencePositionType.Left
										? meta.left
										: productSide.printReferencePosition === GQLProductSidePrintReferencePositionType.Right
										? productSide.printWidth - (meta.right || 0)
										: meta.top) || 0) + productSide.printReferenceOffset}
									mm
								</p>
							</div>
						</div>
					)}
				</>
			)}
			{status === ArtworkStatus.Oversized && (
				<div className="tpb-pill tpb-pill--danger">
					<ExclamationTriangleIcon className="tpb-pill__icon tpb-pill__icon--lg" />
					<div>
						<h5 className="tpb-pill__label">Design too large</h5>
						<p className="tpb-pill__value">Embroidery only supports up to 150mm x 150mm</p>
					</div>
				</div>
			)}
			{status === ArtworkStatus.LowRes && (
				<div className="tpb-pill tpb-pill--warning">
					<ExclamationCircleIcon className="tpb-pill__icon tpb-pill__icon--lg" />
					<div>
						<h5 className="tpb-pill__label">Low resolution image</h5>
						<p className="tpb-pill__value">Your design may appear pixelated when printed</p>
					</div>
				</div>
			)}
		</div>
	);

	return createPortal(pills, overlayLayer);
}
